import type { BusGraph, BusId } from "./buses.js";

/**
 * One reusable voice slot. The gain and panner nodes are created once and live for the entire
 * AudioContext lifetime. Each playback creates a fresh AudioBufferSourceNode (single-use, mandated
 * by Web Audio) wired into the slot's persistent panner→gain chain; on play, the slot's gain
 * connects to the current bus and on `ended` it disconnects.
 */
type VoiceSlot = {
  inUse: boolean;
  bus: BusId | null;
  startedAtSec: number;
  source: AudioBufferSourceNode | null;
  readonly gain: GainNode;
  readonly panner: StereoPannerNode;
  currentBusGain: GainNode | null;
  // Stable closure bound to this slot at construction; reused across plays.
  readonly onSourceEnded: () => void;
};

export type VoicePlayOptions = {
  busId: BusId;
  buffer: AudioBuffer;
  intensity: number;
  pan: number;
  pitchVariance: number;
  startedAtSec: number;
};

/**
 * Pool of reusable audio voices. Pre-builds N slots; `play` picks a free slot (or steals the
 * oldest on the requested bus / globally) and reuses its persistent gain + panner chain.
 * Eliminates per-event GainNode / StereoPannerNode allocation.
 */
export class VoicePool {
  private readonly slots: VoiceSlot[] = [];
  private ctx: AudioContext | null = null;
  private buses: BusGraph | null = null;

  constructor(
    private readonly totalVoices = 24,
    private readonly maxPerBus = 4,
  ) {}

  /** One-time setup at AudioContext unlock. Safe to call repeatedly. */
  initialize(ctx: AudioContext, buses: BusGraph): void {
    if (this.ctx !== null) return;
    this.ctx = ctx;
    this.buses = buses;
    for (let i = 0; i < this.totalVoices; i++) {
      const gain = ctx.createGain();
      const panner = ctx.createStereoPanner();
      panner.connect(gain);
      const slot: VoiceSlot = {
        inUse: false,
        bus: null,
        startedAtSec: 0,
        source: null,
        gain,
        panner,
        currentBusGain: null,
        onSourceEnded: () => {},
      };
      // Bind a stable release closure with a reference to this slot.
      (slot as { onSourceEnded: () => void }).onSourceEnded = () => {
        this.release(slot);
      };
      this.slots.push(slot);
    }
  }

  play(opts: VoicePlayOptions): void {
    const ctx = this.ctx;
    const buses = this.buses;
    if (ctx === null || buses === null) return;
    const busNode = buses.byId.get(opts.busId);
    if (busNode === undefined) return;

    const slot = this.pickSlot(opts.busId);
    if (slot === null) return;
    if (slot.inUse) this.release(slot);

    const source = ctx.createBufferSource();
    source.buffer = opts.buffer;
    source.playbackRate.value = 1 + opts.pitchVariance;
    source.connect(slot.panner);

    slot.gain.gain.value = opts.intensity;
    slot.panner.pan.value = opts.pan;
    slot.gain.connect(busNode.gain);
    slot.currentBusGain = busNode.gain;

    slot.bus = opts.busId;
    slot.startedAtSec = opts.startedAtSec;
    slot.source = source;
    slot.inUse = true;

    source.onended = slot.onSourceEnded;
    source.start();
  }

  /** Number of currently-playing voices (testing aid). */
  size(): number {
    let count = 0;
    for (const slot of this.slots) {
      if (slot.inUse) count += 1;
    }
    return count;
  }

  private release(slot: VoiceSlot): void {
    if (!slot.inUse) return;
    slot.inUse = false;
    slot.bus = null;
    if (slot.currentBusGain !== null) {
      slot.gain.disconnect(slot.currentBusGain);
      slot.currentBusGain = null;
    }
    slot.source = null;
  }

  private pickSlot(busId: BusId): VoiceSlot | null {
    // Single pass collects: free slot, oldest-on-bus, on-bus count, oldest-global.
    let freeSlot: VoiceSlot | null = null;
    let oldestOnBus: VoiceSlot | null = null;
    let oldestOnBusTime = Number.POSITIVE_INFINITY;
    let onBusCount = 0;
    let oldestGlobal: VoiceSlot | null = null;
    let oldestGlobalTime = Number.POSITIVE_INFINITY;

    for (const slot of this.slots) {
      if (!slot.inUse) {
        if (freeSlot === null) freeSlot = slot;
        continue;
      }
      if (slot.bus === busId) {
        onBusCount += 1;
        if (slot.startedAtSec < oldestOnBusTime) {
          oldestOnBusTime = slot.startedAtSec;
          oldestOnBus = slot;
        }
      }
      if (slot.startedAtSec < oldestGlobalTime) {
        oldestGlobalTime = slot.startedAtSec;
        oldestGlobal = slot;
      }
    }

    // Per-bus cap dominates: if we're at maxPerBus already, steal the oldest on bus.
    if (onBusCount >= this.maxPerBus) {
      return oldestOnBus;
    }
    if (freeSlot !== null) return freeSlot;
    // All slots in use globally — steal the oldest across all buses.
    return oldestGlobal;
  }
}
